/**
 * Column types and schemas for `Dataset` (spec §5.2, §5.4).
 *
 * `ColumnType` describes the *contents* of a Dataset column — richer than
 * the runtime `Value` kinds in `value.ts` because it must describe
 * container shapes (`timeseries`, `array`, `struct`) and explicit
 * nullability.
 *
 * `Schema = ReadonlyMap<string, ColumnType>` (spec §5.4): ordered name →
 * type. Map insertion order becomes the Dataset's column order. Each
 * pipeline verb declares a `propagateSchema` function (§5.5) that maps
 * an input Schema to its output Schema.
 *
 * Conventions:
 *   - `nullable` is the explicit nullability wrapper. Bare types assert
 *     no nulls. Pipeline verbs widen conservatively when they may
 *     introduce nulls (left join, filter on missing, aggregation over
 *     empty groups). §5.2.
 *   - `unknown` tags inference failures. Downstream type checks skip
 *     `unknown` columns and emit a warning rather than an error. §5.2.
 *   - `number` vs `integer` preserves origin typing for display
 *     formatting and lossless join semantics, even though all numeric
 *     operations promote to double internally. §5.2.
 *
 * The leaf kinds (`number`, `integer`, …) are grouped behind `LeafKind`
 * so that the listing is the single source of truth: the union, the
 * runtime membership check, and the doc renderer all derive from
 * `LEAF_KINDS`. Adding a new leaf kind is a one-line change.
 */

const LEAF_KINDS = ['number', 'integer', 'string', 'boolean', 'timestamp', 'date', 'null', 'unknown'] as const;

export type LeafKind = (typeof LEAF_KINDS)[number];

const LEAF_KIND_SET: ReadonlySet<string> = new Set(LEAF_KINDS);
// Type-narrowing predicate: tells TypeScript that a `ColumnType` whose
// `kind` is in `LEAF_KINDS` is the leaf-shaped variant of the union. The
// narrowing matters in `describeColumnType` so the exhaustiveness check
// in the switch's default branch knows every compound kind has been
// handled.
const isLeafColumnType = (t: ColumnType): t is Extract<ColumnType, { kind: LeafKind }> => LEAF_KIND_SET.has(t.kind);

export type ColumnType =
  | { readonly kind: LeafKind }
  | { readonly kind: 'timeseries'; readonly value: ColumnType }
  | { readonly kind: 'array'; readonly element: ColumnType }
  | { readonly kind: 'struct'; readonly fields: Readonly<Record<string, ColumnType>> }
  | { readonly kind: 'nullable'; readonly inner: ColumnType }
  | { readonly kind: 'nested-dataset'; readonly innerSchema: Schema };

export type Schema = ReadonlyMap<string, ColumnType>;

// Constructor helpers. Nullary kinds are exposed as shared constants
// (identity-stable, cheap equality). Parameterised kinds are factories.

export const numberCol: ColumnType = { kind: 'number' };
export const integerCol: ColumnType = { kind: 'integer' };
export const stringCol: ColumnType = { kind: 'string' };
export const booleanCol: ColumnType = { kind: 'boolean' };
export const timestampCol: ColumnType = { kind: 'timestamp' };
export const dateCol: ColumnType = { kind: 'date' };
export const nullCol: ColumnType = { kind: 'null' };
export const unknownCol: ColumnType = { kind: 'unknown' };

/**
 * Wrap a type as nullable. Idempotent: `nullableCol(nullableCol(x))`
 * returns `nullableCol(x)`. The wrapper is not applied to itself.
 */
export const nullableCol = (inner: ColumnType): ColumnType =>
  inner.kind === 'nullable' ? inner : { kind: 'nullable', inner };

export const timeseriesCol = (value: ColumnType): ColumnType => ({ kind: 'timeseries', value });

export const arrayCol = (element: ColumnType): ColumnType => ({ kind: 'array', element });

export const structCol = (fields: Readonly<Record<string, ColumnType>>): ColumnType => ({
  kind: 'struct',
  fields,
});

/**
 * A column whose cells are themselves `Dataset`s (Phase 4-prep substrate,
 * D2 + D4). The `innerSchema` describes the schema each cell-`Dataset`
 * carries; per-cell verb dispatch in the evaluator rewrites this when a
 * verb is lifted onto the column.
 *
 * Distinct from `timeseries` (which carries a single value `ColumnType` and
 * implies a `{timestamp, value}` shape) — `nested-dataset` is the substrate
 * shape that holds an arbitrary inner schema. A future change may unify
 * `timeseries<T>` as syntactic sugar for `nested-dataset<{timestamp, value: T}>`,
 * but the two coexist for now so domain code can keep referring to
 * `timeseries` without losing the leaf-value annotation.
 */
export const nestedDatasetCol = (innerSchema: Schema): ColumnType => ({ kind: 'nested-dataset', innerSchema });

// Schema builders.

/**
 * Build a Schema from an entries iterable. Map preserves insertion order;
 * that order becomes the column order of the resulting Dataset.
 */
export const schemaFromEntries = (entries: Iterable<readonly [string, ColumnType]>): Schema => new Map(entries);

/** The empty Schema — for ops that produce no columns. */
export const emptySchema: Schema = new Map();
/**
 * Build an all-`unknown` schema for the given column names. The fallback
 * shape used at boundaries where the caller doesn't carry an explicit
 * schema — test fixtures, and the arquero boundary's verb output before
 * a verb-specific `propagateSchema` is registered.
 */
export const unknownSchemaFor = (names: Iterable<string>): Schema =>
  schemaFromEntries(Array.from(names, (name) => [name, unknownCol]));

// Predicates.

/**
 * Whether a column type permits null values. `null` itself is nullable;
 * `nullable<...>` is nullable; everything else asserts non-null.
 */
export const isNullable = (t: ColumnType): boolean => t.kind === 'null' || t.kind === 'nullable';

/**
 * Strip the `nullable` wrapper, if present. Returns the inner type
 * (asserted non-null) or the input unchanged. Idempotent.
 */
export const unwrapNullable = (t: ColumnType): ColumnType => (t.kind === 'nullable' ? t.inner : t);
/**
 * True iff `t` is a temporal column (`timestamp` / `date`), looking
 * through a `nullable` wrapper. SSOT for "should date-aware handling
 * kick in for this column" — used by the sort verb (instant ordering)
 * and qkg `:where` pushdown (ISO normalization).
 */
export const isTemporalColumn = (t: ColumnType): boolean => {
  const u = unwrapNullable(t);
  return u.kind === 'timestamp' || u.kind === 'date';
};

// Rendering.

/**
 * Human-readable rendering of a `ColumnType`. Used by diagnostics and
 * the LLM-facing documentation surface.
 *
 *   number                          → "number"
 *   nullable(number)                → "number?"
 *   timeseries(number)              → "timeseries<number>"
 *   array(struct{a: number})        → "array<struct{ a: number }>"
 */
export const describeColumnType = (t: ColumnType): string => {
  if (isLeafColumnType(t)) {
    return t.kind;
  }
  switch (t.kind) {
    case 'nullable':
      return `${describeColumnType(t.inner)}?`;
    case 'timeseries':
      return `timeseries<${describeColumnType(t.value)}>`;
    case 'array':
      return `array<${describeColumnType(t.element)}>`;
    case 'struct': {
      const inner = Object.entries(t.fields)
        .map(([k, v]) => `${k}: ${describeColumnType(v)}`)
        .join(', ');
      return `struct{ ${inner} }`;
    }
    case 'nested-dataset':
      return `dataset<${describeSchema(t.innerSchema)}>`;
    default: {
      // Exhaustiveness: leaf kinds are caught by the membership check
      // above; every compound kind has its own case. Adding a new
      // compound kind without a case breaks this assignment.
      const _exhaustive: never = t;
      return _exhaustive;
    }
  }
};

/**
 * Human-readable rendering of a whole Schema. `(empty)` for the empty
 * schema; otherwise `name: type, name: type, ...` in insertion order.
 */
export const describeSchema = (schema: Schema): string => {
  if (schema.size === 0) {
    return '(empty)';
  }
  return Array.from(schema.entries())
    .map(([name, type]) => `${name}: ${describeColumnType(type)}`)
    .join(', ');
};
/**
 * Structural equality on a single `ColumnType`. Discriminator-first, then
 * recursive on wrapper variants:
 *   - leaves equal iff they share `kind`;
 *   - `nullable` / `timeseries` / `array` recurse on their inner type;
 *   - `struct` requires identical field-name sets and recursive equality
 *     of each field's type (object-key order irrelevant);
 *   - `nested-dataset` recurses via `schemasEqual` on the inner schema.
 *
 * Used by the materializer (sub-task 4-prep.4) to check that the
 * verb-chain output schema matches the `LazyDataset`'s declared schema,
 * catching `propagateSchema` bugs at the materialisation boundary.
 */
export const columnTypesEqual = (a: ColumnType, b: ColumnType): boolean => {
  if (a.kind !== b.kind) {
    return false;
  }
  switch (a.kind) {
    case 'nullable':
      return columnTypesEqual(a.inner, (b as { inner: ColumnType }).inner);
    case 'timeseries':
      return columnTypesEqual(a.value, (b as { value: ColumnType }).value);
    case 'array':
      return columnTypesEqual(a.element, (b as { element: ColumnType }).element);
    case 'struct': {
      const bf = (b as { fields: Readonly<Record<string, ColumnType>> }).fields;
      const aEntries = Object.entries(a.fields);
      if (aEntries.length !== Object.keys(bf).length) {
        return false;
      }
      return aEntries.every(([k, aType]) => {
        const bType = bf[k];
        return bType !== undefined && columnTypesEqual(aType, bType);
      });
    }
    case 'nested-dataset':
      return schemasEqual(a.innerSchema, (b as { innerSchema: Schema }).innerSchema);
    default:
      // Leaf — same `kind` is sufficient.
      return true;
  }
};

/**
 * Structural equality on `Schema` (an ordered map of name → ColumnType).
 * Equal iff the two maps have the same set of keys (order-independent —
 * insertion order is meaningful for rendering but not for equivalence)
 * and `columnTypesEqual` for every key's type.
 *
 * SSOT for "is this schema the same shape as that one" — used by the
 * materializer's post-condition check.
 */
export const schemasEqual = (a: Schema, b: Schema): boolean => {
  if (a.size !== b.size) {
    return false;
  }
  for (const [name, type] of a) {
    const other = b.get(name);
    if (other === undefined || !columnTypesEqual(type, other)) {
      return false;
    }
  }
  return true;
};
